import React from 'react';
import { graphql } from 'gatsby';

import SectionItemContentBackgroundContainer from '../SectionItems/SectionItemContentBackground';
import SectionItemProductBuyButtonContainer from '../SectionItems/SectionItemProductBuyButton';
import SectionItemProductImageContainer from '../SectionItems/SectionItemProductImage';
import SectionItemProductTextContainer from '../SectionItems/SectionItemProductText';
import SectionItemProductTitleContainer from '../SectionItems/SectionItemProductTitle';
import SectionProduct from '../../../components/Section/SectionProduct';
import { getBackgroundImage, normalizeContentPositionX, normalizeContentPositionY } from '../utils';

// Types
import { SectionProductContainerFragment } from './__generated__/SectionProductContainerFragment';
import { SectionWidth } from '../../../../types/sections';

export const fragment = graphql`
  fragment SectionProductContainerFragment on CMS_Section {
    ...SectionItemContentBackgroundContainerFragment
    ...SectionItemProductBuyButtonContainerFragment
    ...SectionItemProductImageContainerFragment
    ...SectionItemProductTextContainerFragment
    ...SectionItemProductTitleContainerFragment
    backgroundColor
    backgroundPattern
    contentPositionX
    contentPositionY
    backgroundImage {
      ...SectionBackgroundImageFragment
    }
    contentPageLink {
      slug
    }
  }
`;

type Props = {
  index: number;
  isClipped: boolean;
  isCritical: boolean;
  isHiddenOnMobile: boolean;
  isNeighborClipped: boolean;
  isSectionContentExtraWide: boolean;
  section: SectionProductContainerFragment;
  sectionWidth: SectionWidth;
};

const SectionProductContainer = ({
  index,
  isClipped,
  isCritical,
  isHiddenOnMobile,
  isNeighborClipped,
  isSectionContentExtraWide,
  section,
  sectionWidth,
}: Props) => {
  const backgroundImage = getBackgroundImage(section);

  const contentPositionX = normalizeContentPositionX(section.contentPositionX);
  const contentPositionY = normalizeContentPositionY(section.contentPositionY);

  const contentBackground = <SectionItemContentBackgroundContainer section={section} sectionWidth={sectionWidth} />;

  const button = <SectionItemProductBuyButtonContainer section={section} />;

  const headline = (
    <SectionItemProductTitleContainer contentPositionY={contentPositionY} hasBackgroundImage={!!backgroundImage} section={section} />
  );

  const logo = (
    <SectionItemProductImageContainer
      contentPositionY={contentPositionY}
      isCritical={isCritical}
      isSectionContentExtraWide={isSectionContentExtraWide}
      section={section}
      sectionWidth={sectionWidth}
    />
  );

  const text = (
    <SectionItemProductTextContainer
      contentPositionY={contentPositionY}
      hasBackgroundImage={!!backgroundImage}
      section={section}
      sectionWidth={sectionWidth}
    />
  );

  const shouldHaveNoPadding = !section.headline && !section.logo && !!section.text && contentPositionY === 'center';

  return (
    <SectionProduct
      backgroundColor={section.backgroundColor}
      backgroundImage={backgroundImage}
      backgroundPattern={section.backgroundPattern}
      button={button}
      contentBackground={contentBackground}
      contentPositionX={contentPositionX}
      contentPositionY={contentPositionY}
      headline={headline}
      index={index}
      isClipped={isClipped}
      isCritical={isCritical}
      isHiddenOnMobile={isHiddenOnMobile}
      isNeighborClipped={isNeighborClipped}
      logo={logo}
      sectionWidth={sectionWidth}
      shouldHaveNoPadding={shouldHaveNoPadding}
      text={text}
    />
  );
};

export default SectionProductContainer;
